import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faShip,
  faHotel,
  faHome,
  faStar,
  faCheck,
  faClock,
  faChevronRight,
  faHeart,
  faMapMarkerAlt,
} from '@fortawesome/free-solid-svg-icons';
import type { ILandingPageRenderData } from '@share/common-types';
import { LANDING_DEFAULT_HERO_IMAGE, LANDING_DEFAULT_PRODUCT_IMAGES } from './landing-page-default-assets';
import { ExperiencesGridSection } from './experiences-grid-section';
import { TestimonialsSection } from './testimonials-section';
import { CruiseLinesSection } from './cruise-lines-section';
import styles from './layout-story.module.scss';

interface LayoutStoryProps {
  data: ILandingPageRenderData;
}

interface AccentStyleSet {
  badge: string;
  well: string;
  icon: string;
  orb: string;
}

const ACCENT_STYLES: readonly AccentStyleSet[] = [
  {
    badge: styles.accentBadgeBlue,
    well: styles.accentWellBlue,
    icon: styles.accentIconBlue,
    orb: styles.accentOrbBlue,
  },
  {
    badge: styles.accentBadgeYellow,
    well: styles.accentWellYellow,
    icon: styles.accentIconYellow,
    orb: styles.accentOrbYellow,
  },
  {
    badge: styles.accentBadgeGreen,
    well: styles.accentWellGreen,
    icon: styles.accentIconGreen,
    orb: styles.accentOrbGreen,
  },
] as const;

function ProductIcon({ type, className }: { type: string; className?: string }): JSX.Element {
  const cls = className ?? styles.iconLg;
  if (type.toLowerCase() === 'cruise') return <FontAwesomeIcon icon={faShip} className={cls} />;
  if (type.toLowerCase() === 'hotel') return <FontAwesomeIcon icon={faHotel} className={cls} />;
  return <FontAwesomeIcon icon={faHome} className={cls} />;
}

export function LayoutStory({ data }: LayoutStoryProps): JSX.Element {
  const title = data.customHeadline ?? data.templateName;
  const tagline = data.customSubheadline ?? 'Three incredible getaways. One amazing price.';
  const ctaText = data.customCtaText ?? 'Secure Your Certificate';
  const heroImage = data.templateImageUrl ?? LANDING_DEFAULT_HERO_IMAGE;
  const savingsPercent =
    data.averageRetailPrice > 0 ? Math.round((data.savings / data.averageRetailPrice) * 100) : 0;
  const totalNights = data.products.reduce((acc, p) => acc + p.nights, 0);
  // RSI: parent sets customCtaUrl; do not force BO payment-path fallback
  const ctaHref = data.customCtaUrl;

  const productImages: Record<string, string> = LANDING_DEFAULT_PRODUCT_IMAGES;

  return (
    <div className={styles.root}>
      <section className={styles.hero}>
        <div className={styles.heroMedia}>
          <img src={heroImage} alt="Luxury cruise vacation" />
          <div className={styles.heroOverlay} />
        </div>

        <div className={`container ${styles.heroInner}`}>
          <span className={styles.limitedBadge}>
            <FontAwesomeIcon icon={faHeart} className={styles.iconSm} /> Limited Time Offer
          </span>
          <h1 className={styles.heroTitle}>{title}</h1>
          <p className={styles.heroTagline}>{tagline}</p>

          <div className={styles.productChips}>
            {data.products.map((product, index) => (
              <div key={index} className={styles.productChip}>
                <ProductIcon type={product.type} />
                <span>
                  {product.nights}-Night {product.type}
                </span>
              </div>
            ))}
          </div>

          <p className={styles.heroMeta}>
            <FontAwesomeIcon icon={faClock} className={styles.iconMd} />
            <span>{data.validityMonths} months to redeem</span>
          </p>
        </div>
      </section>

      <section className={styles.valueSection}>
        <div className="container">
          <div className={styles.valueCard}>
            <div className={styles.valueCardGrid}>
              <div>
                <p className={styles.packageLabel}>Total Package Value</p>
                {data.averageRetailPrice > 0 && (
                  <p className={styles.packageStrike}>
                    ${data.averageRetailPrice.toLocaleString()}
                  </p>
                )}
                <div className={styles.packagePriceRow}>
                  <p className={styles.packagePrice}>${data.totalPrice.toLocaleString()}</p>
                  {savingsPercent > 0 && (
                    <span className={styles.saveChip}>Save {savingsPercent}%</span>
                  )}
                </div>
                {totalNights > 0 && (
                  <p className={styles.packageNightly}>
                    That&apos;s just ${Math.round(data.totalPrice / totalNights)}/night for all
                    vacations!
                  </p>
                )}
              </div>
              <div>
                <div className={styles.productLines}>
                  {data.products.map((product, index) => (
                    <div key={index} className={styles.productLine}>
                      <div className={styles.productLineLabel}>
                        <ProductIcon
                          type={product.type}
                          className={`${styles.iconMd} ${styles.productLineIcon}`}
                        />
                        <span>
                          {product.name} &times; {product.nights} nights
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
                <a href={ctaHref} className={styles.ctaButton}>
                  {ctaText}
                  <FontAwesomeIcon icon={faChevronRight} className={styles.iconMd} />
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className={styles.storySection}>
        <div className="container">
          {data.products.map((product, index) => {
            const isEven = index % 2 === 0;
            const imgSrc = productImages[product.type.toLowerCase()] ?? heroImage;
            const accent = ACCENT_STYLES[index % ACCENT_STYLES.length];

            return (
              <div key={index} className={styles.storyBlock}>
                <div
                  className={`${styles.storyMedia} ${
                    isEven ? '' : styles.storyMediaOrderReverse
                  }`}
                >
                  <div className={styles.storyMediaFrame}>
                    <img src={imgSrc} alt={`${product.type} experience`} />
                  </div>
                  <div
                    className={`${styles.storyOrb} ${accent.orb} ${
                      isEven ? styles.storyOrbEnd : styles.storyOrbStart
                    }`}
                  >
                    <ProductIcon type={product.type} className={styles.iconXl} />
                  </div>
                </div>

                <div
                  className={`${styles.storyCopy} ${
                    isEven ? '' : `${styles.storyCopyReverse} ${styles.storyCopyOrderReverse}`
                  }`}
                >
                  <span className={accent.badge}>{product.type} Getaway</span>
                  <h2 className={styles.storyTitle}>
                    {product.nights} Nights of {product.type} Luxury
                  </h2>
                  {data.customDescription && index === 0 ? (
                    <div
                      className="lp-richtext lp-richtext--muted mb-4"
                      dangerouslySetInnerHTML={{ __html: data.customDescription }}
                    />
                  ) : (
                    <p className={styles.storyBody}>
                      Experience {product.nights} nights of premium {product.type.toLowerCase()}{' '}
                      accommodations with world-class amenities and unforgettable service.
                    </p>
                  )}
                  <div className={styles.storyFeatures}>
                    {[
                      `${product.nights} nights included`,
                      'Premium amenities',
                      'Flexible dates',
                      'Concierge service',
                    ].map((label) => (
                      <div key={label} className={styles.storyFeature}>
                        <div className={accent.well}>
                          <FontAwesomeIcon
                            icon={faCheck}
                            className={`${styles.iconMd} ${accent.icon}`}
                          />
                        </div>
                        <span>{label}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      <ExperiencesGridSection data={data} sectionClassName="bg-white" />

      {data.customItems && data.customItems.length > 0 && (
        <section className={styles.destinationsSection}>
          <div className="container">
            <div className={styles.destinationsHeader}>
              <h2 className={styles.destinationsTitle}>Explore Destinations</h2>
            </div>
            <div className={styles.destinationsGrid}>
              {data.customItems.map((item, index) => (
                <div key={index} className={styles.destinationCard}>
                  <div className={styles.destinationMedia}>
                    {item.imageUrl ? (
                      <img src={item.imageUrl} alt={item.title} />
                    ) : (
                      <div className={styles.destinationPlaceholder}>
                        <FontAwesomeIcon icon={faMapMarkerAlt} className={styles.iconXl} />
                      </div>
                    )}
                    <div className={styles.destinationOverlay} />
                  </div>
                  <div className={styles.destinationCaption}>
                    <div className={styles.destinationCaptionInner}>
                      <FontAwesomeIcon
                        icon={faMapMarkerAlt}
                        className={`${styles.iconSm} ${styles.destinationCaptionIcon}`}
                      />
                      <div>
                        <h3 className={styles.destinationCardTitle}>{item.title}</h3>
                        {item.subtitle && (
                          <p className={styles.destinationCardSubtitle}>{item.subtitle}</p>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      <section className={styles.socialProof}>
        <div className="container">
          <div className={styles.socialProofGrid}>
            <div>
              <p className={styles.socialStat}>50K+</p>
              <p className={styles.socialStatLabel}>Happy Travelers</p>
            </div>
            <div>
              <p className={styles.socialStat}>4.9</p>
              <div className={styles.socialStars}>
                {[...Array(5)].map((_, i) => (
                  <FontAwesomeIcon key={i} icon={faStar} className={styles.iconMd} />
                ))}
              </div>
            </div>
            <div>
              <p className={styles.socialStat}>100+</p>
              <p className={styles.socialStatLabel}>Destinations</p>
            </div>
            <div>
              <p className={styles.socialStat}>{totalNights}</p>
              <p className={styles.socialStatLabel}>Nights of Luxury</p>
            </div>
          </div>
        </div>
      </section>

      <CruiseLinesSection data={data} />
      <TestimonialsSection data={data} />

      <section className={styles.finalCta}>
        <div className={styles.finalCtaMedia}>
          <img src={LANDING_DEFAULT_HERO_IMAGE} alt="Sunset beach" />
          <div className={styles.finalCtaOverlay} />
        </div>
        <div className={`container ${styles.finalCtaInner}`}>
          <h2 className={styles.finalCtaTitle}>Ready to Make Memories?</h2>
          <p className={styles.finalCtaSubcopy}>
            {data.products.length} vacations included. One incredible price. Your adventure starts
            now.
          </p>
          {data.averageRetailPrice > 0 && (
            <div className={styles.finalCtaPrices}>
              <p className={styles.finalCtaStrike}>
                ${data.averageRetailPrice.toLocaleString()}
              </p>
              <p className={styles.finalCtaPrice}>${data.totalPrice.toLocaleString()}</p>
            </div>
          )}
          <a href={ctaHref} className={styles.finalCtaButton}>
            {ctaText}
          </a>
          <p className={styles.finalCtaMeta}>
            <FontAwesomeIcon icon={faClock} className={styles.iconSm} />
            Valid for {data.validityMonths} months after purchase
          </p>
        </div>
      </section>
    </div>
  );
}
